/**
 * The CSS size of an element without a layout read per frame (docs/PERF.md "Frame pacing and hitches").
 * `el.clientWidth` after the HUD has written to the DOM in the same frame forces a synchronous style and
 * layout pass; the minimap, the prompt projection, the onboarding pointer and the render system's pixel
 * ratio each read the canvas size every frame, so a frame could lay the page out several times. Here the
 * size is read once and then kept current by a ResizeObserver (which reports after layout, for free).
 * Without ResizeObserver it falls back to the live read.
 */
interface Size {
  w: number;
  h: number;
}

const sizes = new WeakMap<Element, Size>();
let observer: ResizeObserver | null | undefined;

function watch(): ResizeObserver | null {
  if (observer === undefined) {
    observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            for (const e of entries) {
              const s = sizes.get(e.target);
              if (!s) continue;
              const el = e.target as HTMLElement;
              s.w = el.clientWidth;
              s.h = el.clientHeight;
            }
          });
  }
  return observer;
}

/** `{ w: clientWidth, h: clientHeight }` of `el`, cached (the returned object is updated in place). */
export function cssSize(el: HTMLElement): Readonly<Size> {
  let s = sizes.get(el);
  if (s) return s;
  const ro = watch();
  if (!ro) return { w: el.clientWidth, h: el.clientHeight };
  s = { w: el.clientWidth, h: el.clientHeight };
  sizes.set(el, s);
  ro.observe(el);
  return s;
}
